import { useState, useRef, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory } from 'react-router-dom';
import {
  ModalActionButton,
  ModalCancelButton,
  ModalContent,
  ModalContentActions,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  ModalOverlay,
  ModalPortal,
  ModalRoot,
} from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../models/user-generated-contents/context';
import { usePushToastOrFlashMessage } from '../../../components/v2/toaster/deprecation-helper';
import useScreenTitle from '../../../common/use-screen-title';

type UserGeneratedContentFormProps = {
  children: (props: { onSave: () => void; onSubmit: () => void }) => ReactNode;
};

const UserGeneratedContentForm = ({
  children,
}: UserGeneratedContentFormProps) => {
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const confirmedNavigation = useRef(false);
  const {
    userGeneratedContent,
    upsertUserGeneratedContent,
    submitUserGeneratedContent,
    hasChanges,
  } = useUserGeneratedContent();
  useScreenTitle(userGeneratedContent.title || 'New Post');
  const { pushMessage } = usePushToastOrFlashMessage();
  const { t } = useTranslation();
  const history = useHistory();

  const handleActionSuccess = (messageType: 'created' | 'updated') => {
    confirmedNavigation.current = true;
    history.goBack();

    const defaults: Record<typeof messageType, string> = {
      created: 'Post created!',
      updated: 'Post updated!',
    };

    pushMessage({
      text: t(`user_generated_content.${messageType}`, defaults[messageType]),
      type: 'success',
    });
  };

  const handleSubmitFailure = (err: Error) => {
    console.error('UGC draft error', err);

    pushMessage({
      text: t('errors.default'),
      type: 'error',
    });
  };

  const handleSave = () => {
    upsertUserGeneratedContent({
      onSuccess: () =>
        handleActionSuccess(userGeneratedContent.id ? 'updated' : 'created'),
      onError: handleSubmitFailure,
      publicationState: 'draft',
    });
  };

  const handleSubmit = () => {
    submitUserGeneratedContent({
      onSuccess: () =>
        handleActionSuccess(userGeneratedContent.id ? 'updated' : 'created'),
      onError: handleSubmitFailure,
    });
  };

  const handleDiscardChanges = () => {
    if (confirmedNavigation.current) return true;
    setShowDiscardDialog(true);
    return false;
  };

  useEffect(() => {
    if (!hasChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = t('user_generated_content.discard_dialog.message');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges, t]);

  return (
    <>
      <Prompt
        message={handleDiscardChanges}
        when={hasChanges && !confirmedNavigation.current}
      />

      {showDiscardDialog && (
        <ModalRoot
          defaultOpen={true}
          onOpenChange={() => setShowDiscardDialog(false)}
        >
          <ModalPortal>
            <ModalOverlay />
            <ModalContent size="small">
              <ModalContentHeader
                title={t('user_generated_content.discard_dialog.title')}
              />
              <ModalContentBody>
                {t('user_generated_content.discard_dialog.message')}
              </ModalContentBody>
              <ModalContentFooter>
                <ModalContentActions>
                  <ModalCancelButton
                    label={t('common.back')}
                    onClick={() => {
                      setShowDiscardDialog(false);
                    }}
                  />
                  <ModalActionButton
                    variant="danger"
                    label={t('common.discard')}
                    onClick={() => {
                      setShowDiscardDialog(false);
                      confirmedNavigation.current = true;
                      history.goBack();
                    }}
                  />
                </ModalContentActions>
              </ModalContentFooter>
            </ModalContent>
          </ModalPortal>
        </ModalRoot>
      )}

      <form
        aria-label={t('user_generated_content.form_label')}
        onSubmit={(e) => e.preventDefault()}
      >
        {children({
          onSave: handleSave,
          onSubmit: handleSubmit,
        })}
      </form>
    </>
  );
};

export default UserGeneratedContentForm;
